import { Collapse } from "@exopengithub/exokit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";

import type { Document } from "../documents/documentMetadata";
import { DocumentViewer } from "./DocumentViewer";

interface DocumentCardProps {
	document: Document;
}

export const DocumentCard = ({ document }: DocumentCardProps) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);

	const previousRevisions = document.revisions.slice(1);
	const hasPreviousRevisions = previousRevisions.length > 0;

	return (
		<div className="py-4">
			<div className="flex items-start justify-between">
				<div className="flex-1">
					<h3 className="text-base font-semibold text-gray-900">
						{t(document.nameKey)}
					</h3>
					<p className="mt-1 text-sm text-gray-600">
						{t(document.descriptionKey)}
					</p>
					<div className="mt-2 text-sm text-gray-500">
						{new Date(document.currentRevision.date).toLocaleDateString(
							"sv-SE",
							{
								year: "numeric",
								month: "long",
								day: "numeric",
							},
						)}
					</div>
				</div>
				<div className="ml-4">
					<DocumentViewer filePath={document.currentRevision.filePath} />
				</div>
			</div>

			{hasPreviousRevisions && (
				<div className="mt-3">
					<button
						type="button"
						onClick={() => setIsExpanded(!isExpanded)}
						className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
					>
						{isExpanded ? (
							<>
								<IconChevronUp size={16} />
								<span>
									{t("Hide previous versions")} ({previousRevisions.length})
								</span>
							</>
						) : (
							<>
								<IconChevronDown size={16} />
								<span>
									{t("Show previous versions")} ({previousRevisions.length})
								</span>
							</>
						)}
					</button>

					<Collapse in={isExpanded}>
						<div className="mt-3 space-y-2">
							{previousRevisions.map((revision) => (
								<div
									key={revision.date}
									className="flex items-start justify-between rounded-md bg-gray-50 p-3"
								>
									<div className="flex-1">
										<div className="text-sm text-gray-700">
											{new Date(revision.date).toLocaleDateString("sv-SE", {
												year: "numeric",
												month: "long",
												day: "numeric",
											})}
										</div>
									</div>
									<div className="ml-4">
										<DocumentViewer filePath={revision.filePath} />
									</div>
								</div>
							))}
						</div>
					</Collapse>
				</div>
			)}
		</div>
	);
};
